import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  AlertCircle,
  BookOpen,
  ChevronRight,
  Clock,
  FileText,
  Globe,
  Info,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
} from 'lucide-react';
import { PageHeader } from '@/components/admin/cards';
import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from './ui/alert';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  createKnowledgeSource,
  deleteKnowledgeSource,
  listKnowledgeSources,
  previewExistingSource,
  previewKnowledgeSource,
  syncKnowledgeSource,
  type KnowledgeDocumentUpdate,
  type KnowledgeSource,
  type KnowledgeSourceInput,
} from '../lib/knowledge-api';

const emptySource: KnowledgeSourceInput = {
  name: '',
  kind: 'api',
  url: '',
  content: '',
  format: 'auto',
  auth_type: 'none',
  credential: '',
  header_name: 'X-API-Key',
  records_path: '',
  fields: [],
  next_url_path: '',
  max_pages: 1,
};

async function readDocument(file: File): Promise<KnowledgeDocumentUpdate> {
  if (file.size > 2_000_000) throw new Error('Document exceeds 2 MB.');
  if (!/\.(json|ya?ml|html?|md|txt)$/i.test(file.name))
    throw new Error('Upload a JSON, YAML, HTML, Markdown or text document.');
  let content: string;
  try {
    content = new TextDecoder('utf-8', { fatal: true }).decode(await file.arrayBuffer());
  } catch {
    throw new Error('Upload a document encoded as UTF-8.');
  }
  const format = /\.json$/i.test(file.name)
    ? 'json'
    : /\.ya?ml$/i.test(file.name)
      ? 'yaml'
      : /\.html?$/i.test(file.name)
        ? 'html'
        : /\.md$/i.test(file.name)
          ? 'markdown'
          : 'text';
  return { content, format };
}

function errorMessage(error: unknown) {
  if (error !== null && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return 'Could not update knowledge sources. Please try again.';
}

const statusLabels: Record<string, string> = {
  pending: 'Waiting to process',
  syncing: 'Processing',
  ready: 'Ready',
  error: 'Failed',
};

function statusLabel(status: string) {
  return statusLabels[status] ?? status.replaceAll('_', ' ');
}

const processingStarted =
  'Processing started. It will be available to the chatbot once the status shows Ready. Click Refresh status to check.';

function Choice<T extends string>({
  id,
  label,
  value,
  options,
  onChange,
  className,
}: Readonly<{
  id: string;
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onChange: (value: T) => void;
  className?: string;
}>) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={value}
        onValueChange={(selected) => {
          const option = options.find((item) => item.value === selected);
          if (option) onChange(option.value);
        }}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const formatOptions = [
  { value: 'auto', label: 'Detect automatically' },
  { value: 'json', label: 'JSON / OpenAPI JSON' },
  { value: 'yaml', label: 'YAML / OpenAPI YAML' },
  { value: 'html', label: 'HTML' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'text', label: 'Plain text' },
] as const;

const formatLabels: Record<string, string> = {
  auto: 'Auto-detected format',
  json: 'JSON',
  yaml: 'YAML',
  html: 'HTML',
  markdown: 'Markdown',
  text: 'Plain text',
};

const statusVariants: Record<string, 'success' | 'destructive' | 'warning' | 'secondary'> = {
  ready: 'success',
  error: 'destructive',
  review_required: 'warning',
};

function StatusBadge({ status }: Readonly<{ status: KnowledgeSource['status'] }>) {
  const busy = status === 'pending' || status === 'syncing';
  return (
    <Badge data-testid="source-status" role="status" variant={statusVariants[status] ?? 'secondary'}>
      {busy && <Loader2 className={status === 'syncing' ? 'animate-spin' : undefined} aria-hidden="true" />}
      {statusLabel(status)}
    </Badge>
  );
}

const documentHint = 'JSON, YAML, HTML, Markdown or plain text, up to 2 MB.';

export function KnowledgeSources({ onDirtyChange }: Readonly<{ onDirtyChange?: (dirty: boolean) => void }>) {
  const [form, setForm] = useState<KnowledgeSourceInput>(emptySource);
  const [sources, setSources] = useState<KnowledgeSource[]>([]);
  const [documents, setDocuments] = useState<Record<string, KnowledgeDocumentUpdate>>({});
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [deleting, setDeleting] = useState<KnowledgeSource | null>(null);
  const [adding, setAdding] = useState(false);
  const [target, setTarget] = useState<KnowledgeSource | null>(null);

  useEffect(() => {
    onDirtyChange?.(dirty || Object.keys(documents).length > 0);
    return () => onDirtyChange?.(false);
  }, [dirty, documents, onDirtyChange]);

  const reload = useCallback(async () => {
    setSources(await listKnowledgeSources());
  }, []);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const result = await listKnowledgeSources();
        if (active) setSources(result);
      } catch (err) {
        if (active) setError(errorMessage(err));
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, []);

  function update<K extends keyof KnowledgeSourceInput>(key: K, value: KnowledgeSourceInput[K]) {
    setForm((previous) => ({ ...previous, [key]: value }));
    setDirty(true);
  }

  async function action(work: () => Promise<void>) {
    setBusy(true);
    setError('');
    try {
      await work();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function processing(work: () => Promise<void>) {
    setBusy(true);
    setError('');
    const id = toast.loading('Processing…');
    try {
      await work();
      toast.update(id, { render: processingStarted, type: 'success', isLoading: false, autoClose: 5000 });
    } catch (err) {
      toast.update(id, { render: errorMessage(err), type: 'error', isLoading: false, autoClose: 5000 });
    } finally {
      setBusy(false);
    }
  }

  function processSource() {
    void processing(async () => {
      const input = showAdvanced ? form : { ...form, records_path: '', fields: [] };
      const result = await previewKnowledgeSource(input);
      setForm((previous) => ({ ...previous, credential: '' }));
      await createKnowledgeSource({ ...input, credential: '' }, result.preview_hash);
      setForm(emptySource);
      setDirty(false);
      setAdding(false);
      await reload();
    });
  }

  function closeAddSource() {
    if (busy) return;
    setAdding(false);
    setForm(emptySource);
    setDirty(false);
    setError('');
  }

  function processExistingSource(source: KnowledgeSource) {
    void processing(async () => {
      const document = documents[source.id];
      const removeEntry = <T,>(previous: Record<string, T>) => {
        const next = { ...previous };
        delete next[source.id];
        return next;
      };
      const result =
        source.auth_type === 'none'
          ? await previewExistingSource(source.id, document)
          : await previewExistingSource(source.id, document, credentials[source.id]);
      setCredentials(removeEntry);
      await syncKnowledgeSource(source.id, result.preview_hash, document);
      setDocuments(removeEntry);
      setTarget(null);
      await reload();
    });
  }

  const needsKey = (source: KnowledgeSource) => source.kind === 'api' && source.auth_type !== 'none';

  function startProcessing(source: KnowledgeSource) {
    if (source.kind === 'api' && !needsKey(source)) processExistingSource(source);
    else setTarget(source);
  }

  function closeTarget() {
    if (busy || !target) return;
    const id = target.id;
    const removeEntry = <T,>(previous: Record<string, T>) => {
      const next = { ...previous };
      delete next[id];
      return next;
    };
    setCredentials(removeEntry);
    setDocuments(removeEntry);
    setTarget(null);
  }

  const showAdvanced =
    form.kind === 'api' || (Boolean(form.content) && (form.format === 'json' || form.format === 'yaml'));
  const locked = (source: KnowledgeSource) => busy || source.status === 'syncing' || source.status === 'pending';

  return (
    <section data-evo-island className="space-y-6 text-foreground" aria-labelledby="knowledge-heading">
      <PageHeader
        id="knowledge-heading"
        icon={<FileText />}
        title="API & Documents"
        description="Teach the chatbot from your own documentation, policies, menus or FAQs."
      />
      <ol className="grid gap-3 sm:grid-cols-3" aria-label="How it works">
        {[
          ['Add a source', 'Link a public API or upload a document.'],
          ['Click Process', 'We read it and update what the chatbot knows.'],
          ['Wait for Ready', 'Use Refresh status to check progress.'],
        ].map(([title, text], index) => (
          <li key={title} className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
            <span
              className="flex size-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground"
              aria-hidden="true"
            >
              {index + 1}
            </span>
            <span className="text-sm">
              <span className="block font-medium">{title}</span>
              <span className="text-muted-foreground">{text}</span>
            </span>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex min-w-0 flex-1 basis-80 items-start gap-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          Live availability, prices and booking status need a live integration. An OpenAPI JSON/YAML file describes an
          API; it does not fetch its business data.
        </p>
        <Button
          type="button"
          disabled={busy}
          onClick={() => {
            setError('');
            setAdding(true);
          }}
        >
          <Plus aria-hidden="true" />
          Add source
        </Button>
      </div>
      {error && !adding && (
        <Alert variant="destructive" role="alert">
          <AlertCircle aria-hidden="true" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <Dialog open={adding} onOpenChange={(open) => (open ? setAdding(true) : closeAddSource())}>
        <DialogContent data-evo-island className="max-h-[90vh] overflow-y-auto text-foreground sm:max-w-4xl">
          <DialogHeader className="text-left">
            <DialogTitle className="flex items-center gap-2">
              <Plus className="size-4 text-primary" aria-hidden="true" />
              Add a knowledge source
            </DialogTitle>
            <DialogDescription>Link a public HTTPS API or upload a document, then click Process.</DialogDescription>
          </DialogHeader>
          {error && (
            <Alert variant="destructive" role="alert">
              <AlertCircle aria-hidden="true" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <form
            className="min-w-0"
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              processSource();
            }}
          >
            <fieldset disabled={busy} className="min-w-0 space-y-5">
              <legend className="sr-only">Source details</legend>
              <div className="flex flex-wrap items-start gap-4">
                <div className="w-full space-y-2 sm:w-48">
                  <Label htmlFor="knowledge-name">Source name</Label>
                  <Input
                    id="knowledge-name"
                    required
                    maxLength={120}
                    placeholder="e.g. Hotel policies"
                    value={form.name}
                    onChange={(event) => update('name', event.target.value)}
                  />
                </div>
                <Choice
                  id="knowledge-kind"
                  label="Source type"
                  className="w-full sm:w-44"
                  value={form.kind}
                  options={[
                    { value: 'api', label: 'HTTPS API' },
                    { value: 'upload', label: 'Upload document' },
                  ]}
                  onChange={(value) => {
                    setForm((previous) => ({
                      ...previous,
                      kind: value,
                      url: '',
                      content: '',
                      auth_type: 'none',
                      credential: '',
                    }));
                    setDirty(true);
                  }}
                />
                {form.kind === 'upload' && (
                  <>
                    <div className="min-w-0 flex-1 basis-52 space-y-2">
                      <Label htmlFor="knowledge-file">UTF-8 document</Label>
                      <Input
                        id="knowledge-file"
                        type="file"
                        required
                        accept=".json,.yaml,.yml,.html,.htm,.md,.txt"
                        aria-describedby="knowledge-file-hint"
                        className="cursor-pointer file:mr-3 file:rounded-md file:bg-secondary file:px-3 file:text-primary"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (!file) {
                            update('content', '');
                            return;
                          }
                          void action(async () => {
                            update('content', '');
                            const document = await readDocument(file);
                            update('content', document.content);
                            update('format', document.format);
                          });
                        }}
                      />
                      <p id="knowledge-file-hint" className="text-xs text-muted-foreground">
                        {documentHint}
                      </p>
                    </div>
                    <Choice
                      id="knowledge-format"
                      label="Content format"
                      className="w-full sm:w-48"
                      value={form.format}
                      options={formatOptions}
                      onChange={(value) => update('format', value)}
                    />
                  </>
                )}
              </div>
              {form.kind === 'api' && (
                <>
                  <div className="flex flex-wrap items-start gap-4">
                    <div className="min-w-0 flex-1 basis-64 space-y-2">
                      <Label htmlFor="knowledge-url">Public HTTPS API URL</Label>
                      <div className="relative">
                        <Globe
                          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                          aria-hidden="true"
                        />
                        <Input
                          id="knowledge-url"
                          type="url"
                          required
                          maxLength={2048}
                          className="pl-9"
                          value={form.url}
                          placeholder="https://example.com/api/policies"
                          onChange={(event) => update('url', event.target.value)}
                        />
                      </div>
                    </div>
                    <Choice
                      id="knowledge-auth"
                      label="Authentication"
                      className="w-full sm:w-44"
                      value={form.auth_type}
                      options={[
                        { value: 'none', label: 'None' },
                        { value: 'bearer', label: 'Bearer token' },
                        { value: 'api_key', label: 'API key header' },
                      ]}
                      onChange={(value) => {
                        update('auth_type', value);
                        if (value === 'none') update('credential', '');
                      }}
                    />
                    <Choice
                      id="knowledge-format"
                      label="Content format"
                      className="w-full sm:w-52"
                      value={form.format}
                      options={formatOptions}
                      onChange={(value) => update('format', value)}
                    />
                  </div>
                  {form.auth_type !== 'none' && (
                    <div className="flex flex-wrap items-start gap-4">
                      <div className="w-full space-y-2 sm:w-72">
                        <Label htmlFor="knowledge-credential">API credential</Label>
                        <Input
                          id="knowledge-credential"
                          type="password"
                          autoComplete="off"
                          required
                          aria-describedby="knowledge-credential-hint"
                          value={form.credential}
                          onChange={(event) => update('credential', event.target.value)}
                        />
                        <p
                          id="knowledge-credential-hint"
                          className="flex items-start gap-1.5 text-xs text-muted-foreground"
                        >
                          <Lock className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
                          Used only while processing and never saved. Enter it again next time you update this source.
                        </p>
                      </div>
                      {form.auth_type === 'api_key' && (
                        <div className="w-full space-y-2 sm:w-56">
                          <Label htmlFor="knowledge-header">API key header name</Label>
                          <Input
                            id="knowledge-header"
                            required
                            value={form.header_name}
                            onChange={(event) => update('header_name', event.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
              {showAdvanced && (
                <details className="group rounded-lg border border-dashed border-border">
                  <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-3 text-sm font-medium [&::-webkit-details-marker]:hidden">
                    <ChevronRight className="size-4 transition-transform group-open:rotate-90" aria-hidden="true" />
                    Optional JSON/YAML extraction and pagination
                    <span className="font-normal text-muted-foreground">(advanced)</span>
                  </summary>
                  <div className="grid gap-4 border-t border-dashed border-border p-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="knowledge-path">Records JSON Pointer</Label>
                      <Input
                        id="knowledge-path"
                        placeholder="/data/items"
                        value={form.records_path}
                        onChange={(event) => update('records_path', event.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="knowledge-fields">Record fields (comma separated; optional)</Label>
                      <Input
                        id="knowledge-fields"
                        placeholder="title, description, answer"
                        value={form.fields.join(', ')}
                        onChange={(event) =>
                          update(
                            'fields',
                            event.target.value.split(',').map((field) => field.trim()),
                          )
                        }
                        onBlur={() => update('fields', form.fields.filter(Boolean))}
                      />
                    </div>
                    {form.kind === 'api' && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="knowledge-next">Next-page URL JSON Pointer</Label>
                          <Input
                            id="knowledge-next"
                            placeholder="/links/next"
                            value={form.next_url_path}
                            onChange={(event) => update('next_url_path', event.target.value)}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="knowledge-pages">Maximum pages (1–10, on the same API host)</Label>
                          <Input
                            id="knowledge-pages"
                            type="number"
                            min={1}
                            max={10}
                            value={form.max_pages}
                            onChange={(event) => update('max_pages', Number(event.target.value))}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </details>
              )}
              <div className="flex flex-col-reverse gap-2 border-t border-border pt-4 sm:flex-row sm:justify-end">
                <Button type="button" variant="outline" onClick={closeAddSource}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={busy || (form.kind === 'upload' && !form.content)}
                  onClick={(event) => {
                    event.preventDefault();
                    if (event.currentTarget.form?.reportValidity()) processSource();
                  }}
                >
                  {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : <Sparkles aria-hidden="true" />}
                  {busy ? 'Processing…' : form.kind === 'upload' ? 'Process document' : 'Process API'}
                </Button>
              </div>
            </fieldset>
          </form>
        </DialogContent>
      </Dialog>
      <section aria-labelledby="saved-sources-heading" className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 id="saved-sources-heading" className="flex items-center gap-2 text-base font-semibold">
            Saved sources
            {sources.length > 0 && (
              <Badge variant="secondary" aria-label={`${sources.length} sources`}>
                {sources.length}
              </Badge>
            )}
          </h3>
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => void action(reload)}>
            <RefreshCw aria-hidden="true" />
            Refresh status
          </Button>
        </div>
        {!sources.length && (
          <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border px-6 py-10 text-center">
            <span
              className="flex size-10 items-center justify-center rounded-full bg-secondary text-primary"
              aria-hidden="true"
            >
              <BookOpen className="size-5" />
            </span>
            <p className="font-medium">No knowledge sources have been added.</p>
            <p className="text-sm text-muted-foreground">Click Add source to link an API or upload a document.</p>
          </div>
        )}
        {sources.length > 0 && (
          <ul className="grid grid-cols-[repeat(auto-fill,minmax(16rem,1fr))] gap-4">
            {sources.map((source) => (
              <li key={source.id} className="flex">
                <article
                  aria-labelledby={`knowledge-source-${source.id}`}
                  className="flex w-full flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-xs transition-shadow hover:border-primary/40 hover:shadow-md"
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={cn(
                        'flex size-9 shrink-0 items-center justify-center rounded-lg',
                        source.kind === 'api' ? 'bg-secondary text-primary' : 'bg-muted text-muted-foreground',
                      )}
                      aria-hidden="true"
                    >
                      {source.kind === 'api' ? <Globe className="size-4" /> : <FileText className="size-4" />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h4 id={`knowledge-source-${source.id}`} className="truncate text-sm font-semibold">
                          {source.name}
                        </h4>
                        <StatusBadge status={source.status} />
                      </div>
                      <p
                        className="truncate text-xs text-muted-foreground"
                        title={source.kind === 'api' ? source.url : undefined}
                      >
                        {source.kind === 'api' ? source.url : 'Uploaded file'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-end gap-5">
                    <p data-testid="source-sections" className="flex flex-col">
                      <span className="text-2xl leading-none font-bold">{source.chunk_count}</span>{' '}
                      <span className="text-xs text-muted-foreground">sections</span>
                    </p>
                    <div className="flex min-w-0 flex-col items-start gap-1 text-xs text-muted-foreground">
                      <p className="flex items-center gap-1">
                        <span className="sr-only">Resource type:</span>
                        <Badge variant="outline" data-testid="source-type">
                          {source.kind === 'api' ? <Globe aria-hidden="true" /> : <FileText aria-hidden="true" />}
                          {source.kind === 'api' ? 'API' : 'Document'}
                        </Badge>
                      </p>
                      <p className="flex items-center gap-1">
                        <Clock className="size-3.5 shrink-0" aria-hidden="true" />
                        {source.last_synced_at
                          ? new Date(source.last_synced_at).toLocaleString(undefined, {
                              dateStyle: 'medium',
                              timeStyle: 'short',
                            })
                          : 'Not processed yet'}
                      </p>
                    </div>
                  </div>
                  {source.last_error && (
                    <p className="flex items-start gap-2 rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
                      <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                      {source.last_error}
                    </p>
                  )}
                  <div className="mt-auto flex items-center justify-between gap-2 border-t border-dashed border-border pt-3">
                    <span className="flex min-w-0 items-center gap-1 truncate text-xs text-muted-foreground">
                      {needsKey(source) && <Lock className="size-3 shrink-0" aria-hidden="true" />}
                      {formatLabels[source.format] ?? source.format}
                      {needsKey(source) && (source.auth_type === 'bearer' ? ' · Bearer token' : ' · API key header')}
                    </span>
                    <div className="flex shrink-0 items-center gap-1">
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        title="Remove"
                        className="size-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        disabled={locked(source)}
                        onClick={() => setDeleting(source)}
                      >
                        <Trash2 aria-hidden="true" />
                        <span className="sr-only">Remove</span>
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={locked(source)}
                        onClick={() => startProcessing(source)}
                      >
                        <RefreshCw aria-hidden="true" />
                        Process
                      </Button>
                    </div>
                  </div>
                </article>
              </li>
            ))}
          </ul>
        )}
      </section>
      <Dialog open={target !== null} onOpenChange={(open) => !open && closeTarget()}>
        <DialogContent data-evo-island className="text-foreground sm:max-w-md">
          {target && (
            <>
              <DialogHeader className="text-left">
                <DialogTitle>Process {target.name}</DialogTitle>
                <DialogDescription>
                  {target.kind === 'api'
                    ? 'Enter the current API key or token. It is used for this update only and is never saved.'
                    : 'Choose a new file to replace the document, or leave it empty to process the current one again.'}
                </DialogDescription>
              </DialogHeader>
              <form
                className="space-y-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  processExistingSource(target);
                }}
              >
                {needsKey(target) && (
                  <div className="space-y-2">
                    <Label htmlFor={`knowledge-credential-${target.id}`}>
                      API credential<span className="sr-only"> for {target.name}</span>
                    </Label>
                    <Input
                      id={`knowledge-credential-${target.id}`}
                      type="password"
                      autoComplete="off"
                      required
                      disabled={busy}
                      value={credentials[target.id] ?? ''}
                      onChange={(event) =>
                        setCredentials((previous) => ({ ...previous, [target.id]: event.target.value }))
                      }
                    />
                  </div>
                )}
                {target.kind === 'upload' && (
                  <div className="space-y-2">
                    <Label htmlFor={`knowledge-replacement-${target.id}`}>
                      Replace document<span className="sr-only"> for {target.name}</span>
                    </Label>
                    <Input
                      id={`knowledge-replacement-${target.id}`}
                      type="file"
                      accept=".json,.yaml,.yml,.html,.htm,.md,.txt"
                      aria-describedby={`knowledge-replacement-hint-${target.id}`}
                      className="cursor-pointer file:mr-3 file:rounded-md file:bg-secondary file:px-3 file:text-primary"
                      disabled={busy}
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        const id = target.id;
                        setDocuments((previous) => {
                          const next = { ...previous };
                          delete next[id];
                          return next;
                        });
                        if (file) {
                          void action(async () => {
                            const document = await readDocument(file);
                            setDocuments((previous) => ({ ...previous, [id]: document }));
                          });
                        }
                      }}
                    />
                    <p id={`knowledge-replacement-hint-${target.id}`} className="text-xs text-muted-foreground">
                      Optional. {documentHint}
                    </p>
                  </div>
                )}
                <div className="flex justify-end gap-2 border-t border-border pt-4">
                  <Button type="button" variant="outline" disabled={busy} onClick={closeTarget}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={busy || (needsKey(target) && !credentials[target.id])}>
                    <RefreshCw aria-hidden="true" />
                    Process &amp; publish
                  </Button>
                </div>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
      <AlertDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The chatbot will stop using this source once the update finishes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const source = deleting;
                setDeleting(null);
                if (source)
                  void action(async () => {
                    await deleteKnowledgeSource(source.id);
                    await reload();
                  });
              }}
            >
              Remove source
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
